import { ReactElement, useEffect, useState } from "react";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import Chart from "../../../../components/charts/chart";
import { IDictionary } from "../../../../types";
import {
  comparisonType,
  DashboardSchemaType,
  IAcquisitionChartConfig, IAcquisitionDataSchema,
  IAcquisitionWrapperProps,
  IAnnotation,
  IEventData, ILimit
} from "../../types";
import * as d3 from "d3";
import {
  AxisTypes,
  IAxisLinear,
  IPoint,
  ISeriesWithAxes,
  IScaleViewPort
} from "../../../../components/charts/types";
import AxisHorizontal from "../../../../components/charts/AxisHorizontal";
import AxisVertical from "../../../../components/charts/AxisVertical";
import Grid from "../../../../components/charts/grid";
import Legend from "../../../../components/charts/legend";
import { formatDateTimeWithSeconds, formatTimeWithSeconds } from "../../../../system/utils";
import { processConditional } from "./utils";
import HtmlTooltip from "../../../../components/charts/tooltip";
import ToolboxArea from "./toolboxArea";
import { formats } from "../../../../system/formats";
import { IconButton, Tooltip } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getPalette } from "../../../../configuration/configuration";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { DateTime } from "luxon";

interface IAnnotationData {
  config: IAnnotation;
  points: IPoint[];
}

interface ILimitData {
  config: ILimit;
  points: IPoint[];
}

interface IAnnotationEx {
  annotations: IDictionary<IAnnotationData[]>;
}

interface ILimitEx {
  limits: IDictionary<ILimitData[]>;
}

const processAnnotation = (annotation: IAnnotation, points: IPoint[], begin?: number, end?: number): IPoint[] => {
  const result: IPoint[]  = [];

  if (points.length ===  0){
    return result;
  }

  if (annotation.comparison === "MIN" || annotation.comparison === "MAX") {
    let pointer: IPoint | null = null; // points[0];

    for (const point of points) {
      if ((!begin || begin >= point.y) && (!end || end <= point.y)) {
        pointer = pointer ?? point;
        if ((annotation.comparison === "MIN" && point.y < pointer.y) || (annotation.comparison === "MAX"  && point.y > pointer.y))  {
          pointer = point;
        }
      }
    }

    if(pointer) {
      result.push(pointer);
    }
  } else {
    let flag = false;

    for (const point of points) {
      if ((!begin || begin >= point.y) && (!end || end <= point.y)) {
        if (processConditional(point, annotation.comparison as comparisonType, annotation.value)) {
          if (!flag) {
            result.push(point);
            flag = true;
          }
        } else {
          flag = false;
        }
      }
    }
  }

  return result;
};

const getLimit = (minimum: number, maximum: number) => Math.abs(maximum - minimum); // minimum + (maximum - minimum);

const AcquisitionChart = ({ parentSize, data, cell, toolbox, onZoomArea, schemas, fontSize, disableLineClass, xAxisFormatTick, adjustAxes }: IAcquisitionWrapperProps): ReactElement => {
  const acquisitionSchema = schemas[DashboardSchemaType.Acquisition] as IAcquisitionDataSchema;
  const [ lineID ] = useState(useUniqueId("acquisition-chart"));
  const [ showAxes, setShowAxes ] = useState<boolean>(false);
  const [ chartConfig, setChartConfig ] = useState<IAcquisitionChartConfig & IAnnotationEx & ILimitEx | null>(null);
  const [ visibility, setVisibility ]  = useState<IDictionary<boolean>>({});
  const [ yZoomRange, setYZoomRange ] = useState<number[]>([0.0, 1.0]);
  const [ yScales, setYScales ] = useState<IDictionary<{ axis: d3.ScaleLinear<number, number>, config: AxisTypes, index: number, }>>({});
  const [ ref, dimensions ] = useChartDimensions<HTMLDivElement>({
    height: parentSize.height,
    width:   parentSize.width,
    marginBottom: 40 * (fontSize ?? 1),
    marginLeft: 10,
    marginRight: !showAxes || !chartConfig ? 10 : cell.stackedAxes ? 60 : chartConfig.yAxis.length * 67,
    marginTop: toolbox.legend ? 30 * (fontSize ?? 1) : 10
  });

  const stackedDimensions = { ...dimensions };
  if (chartConfig && cell.stackedAxes && chartConfig.yAxis.length >  0)  {
    stackedDimensions.boundedHeight = (stackedDimensions.boundedHeight - (chartConfig.yAxis.length - 1) * 15) /chartConfig.yAxis.length;
  }

  useEffect(() => {
    const now = DateTime.now();
    const maxXAllowed = now.toSeconds();
    let minimumX = Number.MAX_VALUE;
    let maximumX = Number.MIN_VALUE;

    if (!data.variables) {
      return;
    }

    const yAxes: Record<string, IAxisLinear> = {};
    const annotations: IDictionary<IAnnotationData[]> = {};
    const limits: IDictionary<ILimitData[]> = {};

    const series: ISeriesWithAxes[] = cell.variables.map(v => {
      const points = data.variables[v.hash] as IPoint[] ?? [];

      const xs = points.map(p => p.x as number);
      const ys = points.map(p => p.y as number);
      const minimumY = Math.min(...ys);
      const maximumY = Math.max(...ys);

      minimumX = Math.min(minimumX, ...xs);
      maximumX = Math.max(maximumX, ...xs);
      maximumX = Math.min(maximumX, maxXAllowed);

      // console.log("AcquisitionChart>", maximumX, maxXAllowed);

      if (!yAxes[v.yAxis.name]) {
        const minimumAxis = adjustAxes ? minimumY : v.yAxis.minimum + getLimit(v.yAxis.minimum, v.yAxis.maximum) * yZoomRange[0];
        const maximumAxis = adjustAxes ? maximumY : v.yAxis.maximum - getLimit(v.yAxis.minimum, v.yAxis.maximum) * (1 - yZoomRange[1]);

        yAxes[v.yAxis.name] = {
          name: v.yAxis.name,
          formatTick: (n: number | { value: number, } | string | IPoint) => (n as number).toFixed(v.decimals),
          minimum: minimumAxis,
          maximum: maximumAxis,
          ticksCount: v.yAxis.ticksCount
        };
      } else if (adjustAxes) {
        const vMin = Math.min(minimumY, yAxes[v.yAxis.name].minimum);
        const vMax = Math.max(maximumY, yAxes[v.yAxis.name].maximum);

        yAxes[v.yAxis.name].minimum = vMin;
        yAxes[v.yAxis.name].maximum = vMax;
      }

      if(v.annotations && v.annotations.length > 0) {
        annotations[v.title] = v.annotations.map<IAnnotationData>(a => ({
          config: a,
          points: processAnnotation(a, points, v.beginOffset, v.endOffset)
        }));
      }

      v.limits.forEach(l => {
        if (l.type === "VARIABLE" && l.variable) {
          const limitPoints = data.variables[`limit-${l.variable}`] as IPoint[];

          if (!limits[v.hash]) {
            limits[v.hash] = [];
          }

          limits[v.hash].push({
            config: l,
            points: limitPoints
          });

          if (adjustAxes) {
            const ls = limitPoints.map(p => p.y as number);
            const vMin = Math.min(...ls, yAxes[v.yAxis.name].minimum);
            const vMax = Math.max(...ls, yAxes[v.yAxis.name].maximum);

            yAxes[v.yAxis.name].minimum = vMin;
            yAxes[v.yAxis.name].maximum = vMax;
          }
        } else if (adjustAxes) {
          const vMin = Math.min(l.value, yAxes[v.yAxis.name].minimum);
          const vMax = Math.max(l.value, yAxes[v.yAxis.name].maximum);

          yAxes[v.yAxis.name].minimum = vMin;
          yAxes[v.yAxis.name].maximum = vMax;
        }
      });

      return {
        hash: v.hash,
        name: v.title ?? v.field,
        value: points,
        color: v.color,
        unit: v.unit ?? undefined,
        xAxisName: "Time",
        yAxisName: v.yAxis.name
      };
    });

    const events: IEventData[] = cell.events.map(ev => {
      if (data.events[ev.hash]) {
        const dates = data.events[ev.hash].map((i: any) => i.begin);
        minimumX = Math.min(minimumX, ...dates);
        maximumX = Math.max(maximumX, ...dates);
      }

      return {
        title: ev.field,
        color: ev.color,
        type: ev.type,
        data: data.events[ev.hash]
      };
    });

    setChartConfig({
      // width: 300,
      height: parentSize.height,
      xAxis: {
        name: "Time",
        minimum: minimumX,
        maximum: maximumX,
        ticksCount: 5,
        formatTick: xAxisFormatTick ?? ((value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number))
      },
      yAxis: Object.values(yAxes),
      series,
      events,
      showLegend: true,
      annotations,
      limits
    });

    const visSeries: IDictionary<boolean> = {};

    series.forEach(s => visSeries[s.name] = true );
    events.forEach(e => visSeries[`event-${e.title}`] = true);

    setVisibility(visSeries);

  }, [ data, cell, yZoomRange, adjustAxes ]);

  useEffect(() => {
    if (!toolbox.zoom) {
      setYZoomRange([0.0, 1.0]);
    }
  }, [ toolbox ]);

  useEffect(() => {
    if (!chartConfig || !chartConfig.yAxis.length) {
      return;
    }

    const newScales: IDictionary<{ axis: d3.ScaleLinear<number, number>, config: AxisTypes, index: number, }> = {};

    chartConfig.yAxis.forEach((axis, index) => {
      const min = (axis as IAxisLinear).minimum;
      const max = (axis as IAxisLinear).maximum;
      let span = Math.abs(max - min) * 0.1;
      span = span === 0 ? 1 : span;

      newScales[axis.name] = {
        axis:d3.scaleLinear()
          .domain([min - (adjustAxes ? span : 0), max + (adjustAxes ? span : 0)])
          .range([stackedDimensions.boundedHeight, 0])
          .nice(),
        config: axis,
        index
      };
    });

    setYScales(newScales);
  }, [ chartConfig ]);

  if (!chartConfig || !chartConfig.yAxis.length || !yScales[chartConfig.yAxis[0].name]) {
    return <></>;
  }

  const xScale = d3.scaleLinear()
    .domain([(chartConfig.xAxis as IAxisLinear).minimum, (chartConfig.xAxis as IAxisLinear).maximum])
    .range([0, dimensions.boundedWidth]);

  const xAccessor = (d: IPoint): number => d.x as number;
  const yAccessor = (d: IPoint): number => d.y;
  const xAccessorScaled = (d: IPoint) => xScale(xAccessor(d)) ?? 0;
  const yAccessorScaled = (key: string, d: IPoint) => yScales[key].axis(yAccessor(d) as any) ?? 0;

  const xTicks = getTicks(xScale.domain() as number[], (chartConfig.xAxis as IAxisLinear).ticksCount);
  const yTicks = getTicks(yScales[chartConfig.yAxis[0].name].axis.domain() as number[], (chartConfig.yAxis[0] as IAxisLinear).ticksCount + 2);


  let yIndex = -1;

  const updateVisibility = (seriesName: string): void  => {
    setVisibility({ ...visibility, [seriesName]: !visibility[seriesName] });
  };

  const onZoomAreaEx = (from: number, to: number, y0: number, y1: number): void  => {
    const diff = yZoomRange[1]  - yZoomRange[0];
    if (diff > 0) {
      const y00 =  Math.round(100 * (yZoomRange[0] + y0 * diff)) / 100;
      const y11 = Math.round(100 * (yZoomRange[0] + y1 * diff)) / 100;

      setYZoomRange([y00, y11]);
      onZoomArea(from, to);
    }
  };

  const updateScaleDomain = (config: AxisTypes, viewPort: IScaleViewPort) => {
    if (adjustAxes){
      return;
    }
    const newScales = { ...yScales };

    if (viewPort.action === "OFFSET") {
      const domain = newScales[config.name].axis.domain() as number[];
      const min = domain[0];
      const max = domain[1];

      newScales[config.name].axis.domain([ min + viewPort.offset, max + viewPort.offset ]);
    } else if (viewPort.action === "SCALE") {
      const domain = newScales[config.name].axis.domain() as number[];
      const min = domain[0];
      const max = domain[1];
      const factor = (max - min) / dimensions.boundedHeight;
      const scale = (1 - Math.min(2, Math.max(0.5, viewPort.scale))) * factor;

      newScales[config.name].axis.domain([ min - scale, max + scale ]);
    } else {
      const min = (config as IAxisLinear).minimum;
      const max = (config as IAxisLinear).maximum;

      newScales[config.name].axis.domain([ min, max ]);
    }
    setYScales(newScales);
  };

  const eventLegend = chartConfig.events.map(e => ( { name: e.title, color: e.color, type: e.title, } ));

  return (
    <div ref={ref} style={{ position: "relative" }} >
      <IconButton style={{ position: "absolute", right: -5, top: -10 }} onClick={() => setShowAxes(!showAxes)} size="small">
        <Tooltip title={showAxes ? "Hide Axes" : "Show Axes"}>
          {showAxes
            ? <ArrowForwardIosIcon style={{ color: getPalette().statusColor.info.background }}/>
            : <ArrowBackIosIcon style={{ color: getPalette().statusColor.info.background }}/>
          }
        </Tooltip>
      </IconButton>
      <Chart dimensions={dimensions} >
        <defs>
          <clipPath id={`cut-off-${lineID}`}>
            <rect x={0} y={0} width={dimensions.boundedWidth} height={dimensions.boundedHeight} fill="red" />
          </clipPath>

          {cell.stackedAxes && Object.values(yScales).map(a => {
            return(
              <clipPath id={`cut-off-${lineID}-${a.index}`} key={`cut-off-${lineID}-${a.index}`} >
                <rect x="0" y={0} width={stackedDimensions.boundedWidth} height={stackedDimensions.boundedHeight}/>
              </clipPath>
            );
          })}
        </defs>

        <AxisHorizontal
          chartID={lineID}
          dimensions={dimensions}
          scale={xScale}
          label={chartConfig.xAxis.name}
          ticks={xTicks}
          formatTick={chartConfig.xAxis.formatTick}
        />

        { showAxes && Object.keys(yScales).map((key) => {
          const yScale = yScales[key].axis;
          const yConfig = yScales[key].config;

          if(yConfig.hidden) {
            return <></>;
          }

          yIndex++;

          return (
            <AxisVertical
              key={`${lineID}-y-axis-${yIndex}`}
              index={yIndex}
              chartID={lineID}
              dimensions={stackedDimensions}
              scale={yScale}
              label={yConfig.name}
              ticks={getTicks(yScale.domain() as number[], (yConfig as IAxisLinear).ticksCount + 2)}
              formatTick={yConfig.formatTick}
              series={chartConfig.series.filter(s => s.yAxisName === yConfig.name)}
              stacked={cell.stackedAxes}
              updateScaleDomain={vp => updateScaleDomain(yConfig, vp)}/>
          );
        })
        }

        {!cell.stackedAxes && <Grid charID={lineID} xScale={xScale} yScale={yScales[chartConfig.yAxis[0].name].axis} dimensions={stackedDimensions} xTicks={xTicks} yTicks={yTicks}/>}

        {cell.stackedAxes && chartConfig.yAxis.map((a, i) => {

          const ticks = getTicks(yScales[a.name].axis.domain() as number[], (a as IAxisLinear).ticksCount + 2);

          return (
            <Grid key={`${lineID}-grid-${i}`} charID={lineID} xScale={xScale} yScale={yScales[a.name].axis} dimensions={stackedDimensions} xTicks={xTicks} yTicks={ticks} index={i}/>
          );
        })}

        {toolbox.legend &&
          <Legend chartID={lineID} series={chartConfig.series}  width={dimensions.boundedWidth}
            visibility={visibility} updateVisibility={updateVisibility} events={eventLegend} fontSize={fontSize}/>
        }


        <g clipPath={`url(#cut-off-${lineID})`}>

          {/*EVENTS*/}
          {chartConfig.events.map((event, index) => {
            if (!visibility[`event-${event.title}`]) {
              return null;
            }

            if (event.type === "VerticalLine") {
              const keyV = `${lineID}-event-line-${index}`;

              return(
                <g key={keyV}>
                  { event.data && event.data.map((evData, evIndex) => {

                    const metaData = acquisitionSchema.events[evData.type].fields;

                    return (
                      <g key={`${keyV}-${evIndex}`}>
                        <line
                          key={`${lineID}-event-${index}-${evIndex}`}
                          fill={event.color}
                          fillOpacity={0.3}
                          stroke={event.color}
                          strokeWidth={2}
                          x1={xScale(evData.begin)}
                          y1={0}
                          x2={xScale(evData.begin)}
                          y2={dimensions.boundedHeight}
                        />

                        <HtmlTooltip
                          title={
                            <div className="display-flex-column">
                              <div className="display-flex-column align-items-center" >
                                <strong>{evData.title}</strong>
                              </div>
                              <div  className="display-flex-row align-items-center" >
                                <strong className="flex-grow-1 mr-2" >Begin</strong>
                                <strong className="flex-grow-0">{formatDateTimeWithSeconds(evData.begin)}</strong>
                              </div>
                              {evData.end &&
                                <div className="display-flex-row align-items-center">
                                  <strong className="flex-grow-1 mr-2">End</strong>
                                  <strong className="flex-grow-0">{formatDateTimeWithSeconds(evData.end)}</strong>
                                </div>}
                              { Object.entries(metaData).map(([ k, v ], i) => {
                                if (!evData.fields[k]) {
                                  return null;
                                }

                                return (
                                  <div key={`${keyV}-${evIndex}-e-${k}-${i}`} className="display-flex-row align-items-center" >
                                    <strong className="flex-grow-1 mr-2" >{v.name}</strong>
                                    <strong className="flex-grow-0">{formats[v.formatName](evData.fields[k])}</strong>
                                  </div>
                                );
                              })
                              }
                            </div>
                          }>
                          <text
                            transform={`translate(${xScale(evData.begin) - 3}, ${dimensions.boundedHeight - 3}) rotate(-90)`}
                            x={0}
                            y={0}
                            textAnchor={"start"}
                            stroke={event.color}
                            fontWeight={400}
                            fontSize={"smaller"}>
                            {evData.title}
                          </text>
                        </HtmlTooltip>
                      </g>
                    );
                  })
                  }
                </g>
              );
            }

            const key = `${lineID}-event-area-${index}`;

            return(
              <g key={key}>
                { event.data && event.data.map((evData, evIndex) => {
                  const metaData = acquisitionSchema.events[evData.type].fields;
                  const isLadle = evData.type === "Ladle" ;

                  return(
                    <g key={`${key}-${evIndex}`}>
                      <rect
                        fill={isLadle ? evData.fields.ladleColor ?? event.color : event.color}
                        fillOpacity={0.3}
                        stroke={isLadle ? evData.fields.ladleColor ?? event.color : event.color}
                        strokeOpacity={0.5}
                        strokeWidth={isLadle ? 0 : 1}
                        x={xScale(evData.begin)}
                        y={0}
                        width={xScale(evData.end ?? evData.begin) - xScale(evData.begin)}
                        height={dimensions.boundedHeight}
                      />
                      <HtmlTooltip
                        title={
                          <div className="display-flex-column">
                            <div className="display-flex-column align-items-center" >
                              <strong>{evData.title}</strong>
                            </div>
                            <div  className="display-flex-row align-items-center" >
                              <strong className="flex-grow-1 mr-2" >Begin</strong>
                              <strong className="flex-grow-0">{formatDateTimeWithSeconds(evData.begin)}</strong>
                            </div>
                            {evData.end &&
                              <div className="display-flex-row align-items-center">
                                <strong className="flex-grow-1 mr-2">End</strong>
                                <strong className="flex-grow-0">{formatDateTimeWithSeconds(evData.end)}</strong>
                              </div>}
                            { Object.entries(metaData).map(([ k, v ], i) => {
                              if (!evData.fields[k] || ["Ladle Color"].includes(v.name ?? "") ) {
                                return null;
                              }

                              return (
                                <div key={`${key}-${evIndex}-e-${k}-${i}`} className="display-flex-row align-items-center" >
                                  <strong className="flex-grow-1 mr-2" >{v.name}</strong>
                                  <strong className="flex-grow-0">{formats[v.formatName](evData.fields[k])}</strong>
                                </div>
                              );
                            })
                            }
                          </div>
                        }>
                        <text
                          transform={isLadle
                            ? `translate(${Math.trunc(xScale(evData.begin) + (xScale(evData.end ?? evData.begin) - xScale(evData.begin)) / 2)}, 14)`
                            : `translate(${Math.trunc(xScale(evData.begin)) - 3}, 3) rotate(-90)`}
                          x={0}
                          y={0}
                          textAnchor={isLadle ? "middle" : "end"}
                          stroke={isLadle ? "whitesmoke" : event.color}
                          fontSize={".8em"}>
                          {evData.title}
                        </text>
                      </HtmlTooltip>
                    </g>
                  );
                })
                }
              </g>
            );
          })
          }

          {/*SERIES*/}
          {chartConfig.series.map((series, index) => {
            if (!visibility[series.name] || !yScales[series.yAxisName]) {
              return null;
            }

            const lineGenerator = d3.line<IPoint>()
              .x(xAccessorScaled)
              .y((d) => yAccessorScaled(series.yAxisName, d))
              .curve(d3.curveLinear);

            const annotations = chartConfig?.annotations[series.name];
            const key = `${lineID}-series-${index}`;
            const yConfig = yScales[series.yAxisName].config;

            return(
              <g key={key}
                clipPath={cell.stackedAxes ? `url(#cut-off-${lineID}-${yScales[series.yAxisName].index})` : undefined }
                transform={`translate(0, ${(cell.stackedAxes ? yScales[series.yAxisName].index : 0) * (stackedDimensions.boundedHeight + 15)})`}>
                <path
                  className={disableLineClass ? "" : "Line"}
                  fill="none"
                  stroke={series.color}
                  strokeWidth={2}
                  d={lineGenerator(series.value as IPoint[]) ?? ""}
                />

                { annotations && annotations.map((a, i) => {
                  const keyA = `${key}-a-${i}`;

                  return (
                    <g key={keyA}>
                      { a.points && a.points.map((p, j) => {
                        const x = xScale(p.x as number);

                        if (a.config.comparison !== "MIN" && a.config.comparison !== "MAX") {
                          return (
                            <g key={`${key}-l-${j}`}>
                              <line
                                x1={x} x2={x} y1={0} y2={dimensions.boundedHeight}
                                strokeWidth={2} stroke={a.config.color} strokeDasharray="2 2"/>
                              <HtmlTooltip
                                title={
                                  <div className="display-flex-column">
                                    <div className="display-flex-column align-items-center" >
                                      <strong>{a.config.title}</strong>
                                    </div>
                                    <div className="display-flex-row align-items-center" >
                                      <strong className="flex-grow-1 mr-2" style={{ color: series.color }}>{series.name}</strong>
                                      <strong className="flex-grow-0">{yConfig.formatTick ? yConfig.formatTick(p.y, 0) : p.y}</strong>
                                      <strong className="flex-grow-0 mr-1 ml-1">{a.config.comparison}</strong>
                                      <strong className="flex-grow-0">{yConfig.formatTick ? yConfig.formatTick(a.config.value, 0) : a.config.value}</strong>
                                    </div>
                                  </div>
                                }>
                                <text
                                  transform={`translate(${x - 3}, ${dimensions.boundedHeight - 3}) rotate(-90)`}
                                  x={0}
                                  y={0}
                                  textAnchor={"start"}
                                  stroke={a.config.color}
                                  fontWeight={400}
                                  fontSize={"smaller"}>
                                  {a.config.title}
                                </text>
                              </HtmlTooltip>
                            </g>
                          );
                        }

                        return (
                          <g key={`${key}-l-${j}`}>
                            <HtmlTooltip
                              title={
                                <div className="display-flex-column">
                                  <div className="display-flex-column align-items-center" >
                                    <strong>{a.config.title}</strong>
                                  </div>
                                  <div className="display-flex-row align-items-center" >
                                    <strong className="flex-grow-1 mr-2" style={{ color: series.color }}>{series.name}</strong>
                                    <strong className="flex-grow-0">{yConfig.formatTick ? yConfig.formatTick(p.y, 0) : p.y}</strong>
                                    {series.unit && <sub>{`[${series.unit}]`}</sub>}
                                  </div>
                                </div>
                              }>
                              {a.config.comparison === "MIN"
                                ? <path d={`M ${x},${dimensions.boundedHeight - 10} ${x - 5},${dimensions.boundedHeight} ${x + 5},${dimensions.boundedHeight} z`}  fill={series.color}/>
                                : <path d={`M ${x},${10} ${x - 5},${0} ${x + 5},${0} z`}  fill={series.color}/>
                              }
                            </HtmlTooltip>
                          </g>
                        );
                      })
                      }
                    </g>
                  );
                })
                }
              </g>
            );
          })
          }

          {/*FIXED ANNOTATIONS*/}
          {cell.annotations && cell.annotations.map((annotation, index) => {

            if (annotation.type === "VerticalLine") {
              const keyV = `${lineID}-fixed-annotation-line-${index}`;

              return (
                <line
                  key={keyV}
                  fill={annotation.color}
                  fillOpacity={0.3}
                  stroke={annotation.color}
                  strokeWidth={2}
                  x1={xScale(annotation.begin)}
                  y1={0}
                  x2={xScale(annotation.begin)}
                  y2={dimensions.boundedHeight}
                />
              );
            }

            const keyA = `${lineID}-fixed-annotation-area-${index}`;

            return(
              <rect
                key={keyA}
                fill={annotation.color}
                fillOpacity={0.3}
                stroke={annotation.color}
                strokeOpacity={0.5}
                strokeWidth={1}
                x={xScale(annotation.begin)}
                y={0}
                width={xScale(annotation.end ?? annotation.begin) - xScale(annotation.begin)}
                height={dimensions.boundedHeight}
              />
            );
          })
          }

          {/*LIMITS*/}
          { cell.variables.map((variable, index) => {
            if (!visibility[variable.title]) {
              return null;
            }

            const key = `${lineID}-limit-${index}`;

            const series = chartConfig.series.find(x => x.hash === variable.hash);
            if (!series) {
              return null;
            }

            const lineGenerator = d3.line<IPoint>()
              .x(xAccessorScaled)
              .y((d) => yAccessorScaled(series.yAxisName, d))
              .curve(d3.curveLinear);

            return(
              <g key={key}
                transform={`translate(0, ${(cell.stackedAxes ? yScales[series.yAxisName].index : 0) * (stackedDimensions.boundedHeight + 15)})`}>
                { variable.limits.map((limit, lIndex) => {

                  if (limit.type === "VARIABLE") {
                    if (!limit.variable || !chartConfig?.limits[variable.hash]) {
                      return null;
                    }

                    const limitData = chartConfig?.limits[variable.hash].find(z => z.config.variable === limit.variable);
                    if (!limitData || !limitData.points || limitData.points.length === 0)  {
                      return null;
                    }

                    const ty = yScales[variable.yAxis.name].axis(limitData.points[0].y);

                    return (
                      <g key={`${key}-${lIndex}`}>
                        <path
                          className={disableLineClass ? "" : "Line"}
                          fill="none"
                          stroke={limit.color}
                          strokeWidth={2}
                          strokeDasharray="2"
                          d={lineGenerator(limitData.points) ?? ""}
                        />

                        <text
                          stroke={limit.color}
                          className="Axis__tick"
                          x={2}
                          y={ty - 3}
                          textAnchor={"start"}>
                          {limit.title}
                        </text>
                      </g>
                    );
                  }


                  let value = limit.value;

                  if (limit.type === "MIN") {
                    value = Math.min(...(series.value as IPoint[]).map(p => p.y));
                  } else if (limit.type === "MAX") {
                    value = Math.max(...(series.value as IPoint[]).map(p => p.y));
                  } else if (limit.type === "AVG") {
                    value = d3.mean((series.value as IPoint[]).map(p => p.y)) ?? 0;
                  }

                  if (!yScales[variable.yAxis.name]) {
                    return null;
                  }

                  const y = yScales[variable.yAxis.name].axis(value);
                  const yConfig = yScales[series.yAxisName].config;

                  return (
                    <g key={`${key}-${lIndex}`}>
                      <line  x1={0} x2={dimensions.boundedWidth} y1={y} y2={y} strokeDasharray="4"
                        stroke={limit.color} strokeWidth={2}/>

                      <HtmlTooltip
                        title={
                          <div className="display-flex-column">
                            <div className="display-flex-column align-items-center" >
                              <strong>{limit.title}</strong>
                            </div>
                            <div className="display-flex-row align-items-center" >
                              <strong className="flex-grow-1 mr-2" style={{ color: series.color }}>{variable.title}</strong>
                              <strong className="flex-grow-0">{yConfig.formatTick ? yConfig.formatTick(value, 0) : value}</strong>
                              {series.unit && <sub>{`[${series.unit}]`}</sub>}
                            </div>
                          </div>
                        }>
                        <text
                          stroke={limit.color}
                          className="Axis__tick"
                          x={2}
                          y={y - 3}
                          textAnchor={"start"}>
                          {limit.title}
                        </text>
                      </HtmlTooltip>
                    </g>
                  );
                })
                }
              </g>
            );
          })
          }

        </g>

        { (toolbox.cursor || toolbox.zoom) &&
          <ToolboxArea lineID={lineID} dimensions={dimensions} toolbox={toolbox} onZoomArea={onZoomAreaEx} chartConfig={chartConfig}
            yScales={yScales} xScale={xScale} stackedAxes={cell.stackedAxes ?? false}/>
        }
      </Chart>
    </div>
  );
};

export default AcquisitionChart;
